import React, { useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ToolBreadcrumb from 'components/Common/ToolBreadcrumb';

import {
  formatVerificationDate,
  recordFieldValue,
  VERIFICATION_FIELD_LABELS,
  VERIFICATION_FIX_LABELS,
  VERIFICATION_RECORD_FIELDS,
  VerificationRecordField,
  verificationNextTitle,
  verificationShowsEvidence,
  verificationStatusLabel,
  VerificationReportCheck,
  VerificationReportCheckTone,
} from './entityVerificationReportModel';
import {
  describeVerificationResult,
  entityStatusLabel,
  hasVerificationResults,
  isAttentionTone,
  resolveVerificationPageState,
  SEVERITY,
} from './verificationReportSummary';

interface EntityVerificationReportViewProps {
  supplier: any;
  databaseName: string;
  databaseId?: string;
  checks: VerificationReportCheck[];
  lastRunAt: string | null;
  /** The database's own verification setting is on. */
  databaseVerificationOn: boolean;
  running: boolean;
  /** The team's Business Verification credits; null when unknown. */
  credits?: { total: number; remaining: number } | null;
  /**
   * Why a run cannot start (no credits left, the database being screened), from
   * verificationRunBlockedReason; disables the run buttons and is their tooltip.
   */
  runBlockedReason?: string | null;
  runStartedAt: number | null;
  /**
   * Only the checks, for the record page's Business Verification tab: the
   * tab's band already carries the answer, the run and the report link.
   */
  embedded?: boolean;
  onBack?: () => void;
  /** Opens Entity Details, focused on `field` when one is given. */
  onEditDetails: (field?: VerificationRecordField) => void;
  onRun: () => void;
  onOpenDatabaseSettings?: () => void;
  /** Downloads this record's verification report as a PDF. */
  onDownloadReport?: () => void;
  downloadingReport?: boolean;
}

type RowTone = VerificationReportCheckTone | 'locked' | 'running';

const sectionHeadingId = (title: string) =>
  `bv-section-${title.toLowerCase().replace(/\s+/g, '-')}`;

const SKELETON_WIDTHS = ['74%', '62%', '80%', '58%', '70%', '66%', '76%'];

const ToneIcon: React.FC<{ tone: RowTone }> = ({ tone }) => {
  if (tone === 'passed') return <CheckCircleOutlineOutlinedIcon />;
  if (tone === 'failed') return <HighlightOffOutlinedIcon />;
  if (tone === 'review' || tone === 'input')
    return <ErrorOutlineOutlinedIcon />;
  if (tone === 'locked') return <LockOutlinedIcon />;
  return <RemoveCircleOutlineOutlinedIcon />;
};

const EntityVerificationReportView: React.FC<
  EntityVerificationReportViewProps
> = ({
  supplier,
  databaseName,
  databaseId,
  checks,
  lastRunAt,
  databaseVerificationOn,
  running,
  credits = null,
  runBlockedReason = null,
  runStartedAt,
  embedded = false,
  onBack,
  onEditDetails,
  onRun,
  onOpenDatabaseSettings,
  onDownloadReport,
  downloadingReport = false,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const state = resolveVerificationPageState({
    supplier: supplier,
    checks: checks,
    // The page redirects a Team without business verification to the record.
    hasPlan: true,
    databaseVerificationOn: databaseVerificationOn,
    running: running,
  });
  const hasResults = hasVerificationResults(checks);
  const isResultState = ['open', 'failed', 'error', 'clean'].includes(state);
  const elapsed = runStartedAt
    ? Math.max(0, Math.floor((now - runStartedAt) / 1000))
    : 0;
  const lastRunLabel = lastRunAt
    ? `Last run ${formatVerificationDate(lastRunAt)}`
    : '';

  const failedChecks = checks.filter((check) => check.tone === 'failed');
  const attentionChecks = checks.filter((check) => isAttentionTone(check.tone));
  const passedChecks = checks.filter((check) => check.tone === 'passed');
  const domainInputs = checks.filter(
    (check) => check.tone === 'input' && check.fix === 'domain',
  ).length;

  // The first check a reviewer needs to act on opens by default; once they
  // toggle a row, their choice wins.
  const defaultExpanded = useMemo(() => {
    if (!['open', 'failed', 'error'].includes(state)) return '';
    const worst = [...checks]
      .filter((check) => check.tone !== 'passed')
      .sort((a, b) => SEVERITY[a.tone] - SEVERITY[b.tone])[0];
    return worst?.id || '';
  }, [checks, state]);
  const openId = expandedId === null ? defaultExpanded : expandedId;

  const entityName = supplier?.name || 'this entity';
  const country = supplier?.country_of_origin || 'Country not set';
  const statusFlag = String(supplier?.status_flag || 'new').toLowerCase();
  const statusLabel = entityStatusLabel(statusFlag);
  const statusTone = ['approved', 'cleared'].includes(statusFlag)
    ? 'cleared'
    : ['rejected', 'blocked', 'barred'].includes(statusFlag)
    ? 'blocked'
    : 'review';

  // ── The answer, once ────────────────────────────────────────────────────
  const { figure, figureTone, tail, summary } = describeVerificationResult({
    state: state,
    supplier: supplier,
    checks: checks,
    databaseName: databaseName,
    elapsedSeconds: elapsed,
  });

  const runLine =
    state === 'dboff'
      ? 'Turned off for this database'
      : state === 'running'
      ? 'Running now'
      : state === 'error'
      ? 'Last attempt did not complete'
      : lastRunLabel || 'Never run';

  // ── Meter: one segment per check, worst first ───────────────────────────
  // A check whose latest attempt could not run dims.
  const meterSegments = [...checks]
    .sort((a, b) => SEVERITY[a.tone] - SEVERITY[b.tone])
    .map((check) => {
      let tone: RowTone = check.tone === 'input' ? 'review' : check.tone;
      if (state === 'running') tone = 'running';
      else if (['dboff', 'never', 'noinput'].includes(state)) tone = 'not-run';
      return { id: check.id, tone: tone, stale: Boolean(check.error) };
    });
  const meterLabel =
    state === 'running'
      ? 'Verifying'
      : ['dboff', 'never', 'noinput'].includes(state)
      ? 'Not run'
      : `${failedChecks.length} failed, ${attentionChecks.length} need attention, ${passedChecks.length} passed, of ${checks.length} checks`;

  // ── Sections ────────────────────────────────────────────────────────────
  interface SectionModel {
    title: string;
    note: string;
    noteTone: RowTone | 'muted';
    rows: VerificationReportCheck[];
  }
  const sections: SectionModel[] = [];
  if (state === 'dboff') {
    sections.push({
      title: 'Seven checks',
      note: '',
      noteTone: 'muted',
      rows: checks,
    });
  } else if (state === 'never') {
    sections.push({
      title: 'Seven checks',
      note: 'One run covers all seven',
      noteTone: 'muted',
      rows: checks,
    });
  } else if (state === 'noinput') {
    sections.push({
      title: 'Seven checks',
      note: 'Each needs at least one field',
      noteTone: 'review',
      rows: checks,
    });
  } else if (state === 'running' && !hasResults) {
    sections.push({
      title: 'Seven checks',
      note: 'Checking all seven',
      noteTone: 'running',
      rows: checks,
    });
  } else {
    const isRunning = state === 'running';
    if (failedChecks.length) {
      sections.push({
        title: 'Failed',
        note: isRunning
          ? 'Checking again'
          : 'Running again will not clear these',
        noteTone: isRunning ? 'running' : 'failed',
        rows: failedChecks,
      });
    }
    if (attentionChecks.length) {
      sections.push({
        title: 'Needs attention',
        note: isRunning
          ? 'Checking again'
          : domainInputs > 1
          ? 'One domain closes two of these'
          : '',
        noteTone: isRunning ? 'running' : domainInputs > 1 ? 'review' : 'muted',
        rows: [...attentionChecks].sort(
          (a, b) => SEVERITY[a.tone] - SEVERITY[b.tone],
        ),
      });
    }
    if (passedChecks.length) {
      sections.push({
        title: 'Passed',
        note: isRunning ? 'Checking again' : lastRunLabel,
        noteTone: isRunning ? 'running' : 'muted',
        rows: passedChecks,
      });
    }
  }

  const rowView = (check: VerificationReportCheck) => {
    let tone: RowTone = check.tone;
    let detail = check.description;
    let pill = verificationStatusLabel(check);
    if (state === 'dboff') {
      tone = 'locked';
      pill = 'Turned off';
      detail = check.about;
    } else if (state === 'never') {
      tone = 'not-run';
      pill = 'Not run';
      detail = check.about;
    } else if (state === 'running') {
      tone = 'running';
      pill = 'Checking';
    } else if (check.error) {
      // The icon keeps the earlier verdict; the pill says why it is not fresh.
      return {
        tone: tone,
        pill: pill,
        pillTone: 'not-run' as RowTone,
        isCheckError: true,
        detail: detail,
      };
    }
    return {
      tone: tone,
      pill: pill,
      pillTone: tone,
      isCheckError: false,
      detail: detail,
    };
  };

  // ── Entity details: the fields a check is waiting on ───────────────────
  // A field blocks when it is empty and a check cannot run without it. The
  // header button says how many, and opens the drawer on the first of them.
  const blockingFields =
    state === 'dboff'
      ? []
      : VERIFICATION_RECORD_FIELDS.filter(
          (field) =>
            !recordFieldValue(supplier, field) &&
            checks.some(
              (check) =>
                check.fix === field &&
                (check.tone === 'input' || state === 'noinput'),
            ),
        );

  const firstMissingField = (
    ['domain', 'email', 'phone', 'address'] as VerificationRecordField[]
  ).find((field) => !recordFieldValue(supplier, field));

  const recordLocation = {
    pathname: `/supplier/${supplier?.id || supplier?.entity_id}`,
    state: databaseId ? { objectData: { viewType: 'Database' } } : undefined,
  };

  // Screen readers hear a run start and finish, not the elapsed seconds.
  const announcedStateRef = useRef(state);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const previous = announcedStateRef.current;
    announcedStateRef.current = state;
    if (previous === state) return;
    if (state === 'running') {
      setAnnouncement(`Verifying ${entityName}.`);
    } else if (previous === 'running') {
      setAnnouncement(`Verification finished. ${figure}, ${tail}.`);
    }
  }, [entityName, figure, state, tail]);

  const checksList = (
    <div className='bv-checks'>
      {sections.map((section) => (
        <section
          className='bv-section'
          aria-labelledby={sectionHeadingId(section.title)}
          key={section.title}
        >
          <div className='bv-section-head'>
            <h2 id={sectionHeadingId(section.title)}>{section.title}</h2>
            <span className='bv-section-count'>{section.rows.length}</span>
            {section.note ? (
              <span className={`bv-section-note bv-tone-${section.noteTone}`}>
                {section.note}
              </span>
            ) : null}
          </div>
          <div className='bv-card bv-list'>
            {section.rows.map((check, index) => {
              const view = rowView(check);
              const expandable = isResultState;
              const isOpen = expandable && openId === check.id;
              const panelId = `bv-${check.id}-panel`;
              const emphasis =
                expandable && check.tone !== 'passed' ? ' is-open-check' : '';
              const rowInner = (
                <>
                  <span className={`bv-check-icon bv-tone-${view.tone}`}>
                    <ToneIcon tone={view.tone} />
                  </span>
                  <span className='bv-check-copy'>
                    <span className='bv-check-label'>{check.label}</span>
                    {state === 'running' ? (
                      <span
                        className='bv-skeleton'
                        style={{
                          width:
                            SKELETON_WIDTHS[index % SKELETON_WIDTHS.length],
                          animationDelay: `${index * 0.12}s`,
                        }}
                      />
                    ) : (
                      <span className='bv-check-detail'>{view.detail}</span>
                    )}
                  </span>
                  <span className={`bv-pill bv-tone-${view.pillTone}`}>
                    {view.pill}
                  </span>
                </>
              );
              return (
                <div
                  className={`bv-check${emphasis}${
                    isOpen ? ' is-expanded' : ''
                  }`}
                  key={check.id}
                >
                  {expandable ? (
                    <button
                      type='button'
                      className='bv-check-row'
                      aria-expanded={isOpen}
                      aria-controls={isOpen ? panelId : undefined}
                      onClick={() => setExpandedId(isOpen ? '' : check.id)}
                    >
                      {rowInner}
                      <KeyboardArrowDownOutlinedIcon className='bv-chevron' />
                    </button>
                  ) : (
                    <div className='bv-check-row is-static'>{rowInner}</div>
                  )}
                  {isOpen ? (
                    <div className='bv-check-panel' id={panelId}>
                      <div>
                        <div className='bv-label'>Read from this record</div>
                        <dl className='bv-reads'>
                          {check.reads.map((field) => {
                            const value = recordFieldValue(supplier, field);
                            return (
                              <div key={field}>
                                <dt>{VERIFICATION_FIELD_LABELS[field]}</dt>
                                <dd
                                  className={
                                    value
                                      ? ''
                                      : check.fix === field
                                      ? 'is-blocking'
                                      : 'is-empty'
                                  }
                                >
                                  {value || 'Not set'}
                                </dd>
                              </div>
                            );
                          })}
                        </dl>
                      </div>
                      <div>
                        <div className='bv-label'>
                          {verificationNextTitle(check)}
                        </div>
                        <p>{check.next}</p>
                        {verificationShowsEvidence(check) ? (
                          <dl className='bv-evidence'>
                            {check.details.map((detail) => (
                              <div key={detail.label}>
                                <dt>{detail.label}</dt>
                                <dd>{detail.value}</dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                        {check.fix && check.tone !== 'passed' ? (
                          <button
                            type='button'
                            className='bv-btn bv-btn-primary bv-btn-sm'
                            onClick={() =>
                              onEditDetails(check.fix || undefined)
                            }
                          >
                            {VERIFICATION_FIX_LABELS[check.fix]}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  if (embedded) {
    return (
      // The tab's band announces a run starting and finishing.
      <div className='bv-page bv-page--embedded'>{checksList}</div>
    );
  }

  return (
    <div className='team-content-container bv-page'>
      <span className='bv-sr-only' role='status' aria-live='polite'>
        {announcement}
      </span>
      <header className='bv-head'>
        <div className='bv-trail'>
          <button type='button' className='bv-back' onClick={onBack}>
            <ArrowBackOutlinedIcon />
            Back to record
          </button>
          <span className='bv-trail-rule' aria-hidden='true' />
          <ToolBreadcrumb
            items={[
              { label: 'BITE DB', to: '/bite-db/entities' },
              ...(databaseId
                ? [{ label: databaseName, to: `/database/${databaseId}` }]
                : []),
              { label: supplier?.name || 'Entity', to: recordLocation },
              { label: 'Business verification' },
            ]}
          />
        </div>

        <div className='bv-title-row'>
          <div className='bv-title-block'>
            <h1>
              <VerifiedUserOutlinedIcon aria-hidden='true' />
              Business verification
            </h1>
            <div className='bv-meta'>
              <strong>{supplier?.name || 'Entity'}</strong>
              <span className={`entity-hub-status ${statusTone}`}>
                <span aria-hidden='true' />
                {statusLabel}
              </span>
              <i aria-hidden='true'>·</i>
              <span>{country}</span>
              <i aria-hidden='true'>·</i>
              <span className={state === 'error' ? 'bv-meta-alert' : ''}>
                {runLine}
              </span>
              <button
                type='button'
                className='bv-details-btn'
                aria-haspopup='dialog'
                onClick={() => onEditDetails(blockingFields[0])}
              >
                <BadgeOutlinedIcon aria-hidden='true' />
                Entity details
                {blockingFields.length ? (
                  <small>{blockingFields.length} needed</small>
                ) : null}
              </button>
            </div>
          </div>

          <div className='bv-actions'>
            {credits && credits.total > 0 ? (
              <span className='bv-actions-note'>
                Remaining credits this month:{' '}
                {credits.remaining.toLocaleString()}
              </span>
            ) : null}
            {state === 'dboff' ? (
              <>
                <span className='bv-actions-note'>
                  Turned off for this database
                </span>
                <button
                  type='button'
                  className='bv-btn bv-btn-primary'
                  onClick={onOpenDatabaseSettings}
                >
                  Open screening settings
                </button>
              </>
            ) : state === 'noinput' ? (
              <>
                <span className='bv-actions-note'>No field to check yet</span>
                <button
                  type='button'
                  className='bv-btn bv-btn-primary'
                  onClick={() => onEditDetails(firstMissingField)}
                >
                  Add details
                </button>
              </>
            ) : state === 'never' ? (
              <button
                type='button'
                className='bv-btn bv-btn-primary'
                onClick={onRun}
                disabled={Boolean(runBlockedReason)}
                title={runBlockedReason || undefined}
              >
                Run verification
              </button>
            ) : state === 'running' ? (
              <span className='bv-running-pill'>
                <span className='bv-beat' aria-hidden='true' />
                Verifying<span aria-hidden='true'> · {elapsed}s</span>
              </span>
            ) : (
              <>
                <button
                  type='button'
                  className='bv-btn bv-btn-ghost'
                  onClick={onDownloadReport}
                  disabled={downloadingReport}
                >
                  <FileDownloadOutlinedIcon />
                  {downloadingReport ? 'Preparing PDF…' : 'Download PDF'}
                </button>
                <button
                  type='button'
                  className='bv-btn bv-btn-ghost'
                  onClick={onRun}
                  disabled={Boolean(runBlockedReason)}
                  title={runBlockedReason || undefined}
                >
                  <RefreshOutlinedIcon />
                  {state === 'error' ? 'Try again' : 'Run again'}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className='bv-layout'>
        <section className='bv-card bv-summary' aria-label='Summary'>
          <div className='bv-summary-figure'>
            <strong className={`bv-tone-${figureTone}`}>{figure}</strong>
            <span>{tail}</span>
          </div>
          <div className='bv-meter' role='img' aria-label={meterLabel}>
            {meterSegments.map((segment, index) => (
              <span
                key={segment.id}
                className={`bv-tone-${segment.tone}${
                  segment.tone === 'running' ? ' bv-beat' : ''
                }${segment.stale ? ' is-stale' : ''}`}
                style={{ animationDelay: `${index * 0.12}s` }}
              />
            ))}
          </div>
          {summary ? <p>{summary}</p> : null}
        </section>

        {checksList}
      </div>
    </div>
  );
};

export const EntityVerificationReportSkeleton: React.FC = () => (
  <div className='team-content-container bv-page' aria-busy='true'>
    <header className='bv-head'>
      <span className='bv-skeleton' style={{ width: 280, height: 14 }} />
      <div className='bv-title-row'>
        <div className='bv-title-block'>
          <span className='bv-skeleton' style={{ width: 300, height: 28 }} />
          <span
            className='bv-skeleton'
            style={{ width: 420, height: 14, marginTop: 12 }}
          />
        </div>
      </div>
    </header>
    <div className='bv-layout'>
      <div className='bv-card bv-summary'>
        <span className='bv-skeleton' style={{ width: 160, height: 24 }} />
        <span
          className='bv-skeleton'
          style={{ width: '100%', height: 6, marginTop: 18 }}
        />
        <span
          className='bv-skeleton'
          style={{ width: '70%', height: 12, marginTop: 18 }}
        />
      </div>
      <div className='bv-checks'>
        <div className='bv-card bv-list'>
          {SKELETON_WIDTHS.map((width, index) => (
            <div className='bv-check-row is-static' key={index}>
              <span className='bv-skeleton' style={{ width: 18, height: 18 }} />
              <span className='bv-check-copy'>
                <span
                  className='bv-skeleton'
                  style={{ width: 180, height: 14 }}
                />
                <span className='bv-skeleton' style={{ width: width }} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default EntityVerificationReportView;
